import React, { FC, useCallback } from 'react'
import { Box, Collapse, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ContractProfileChangeAnswers } from '../../../graphql'
import {
  AddressAutocomplete,
  AddressCorrespondencePropsType,
  AddressPropsType,
  Toggler,
} from '../../Common'
import ControlledTooltipWrapped from '../../Common/Tooltips/ControlledTooltipWrapped'

const useStyles = makeStyles(() => ({
  dirtyControl: {
    position: 'relative',
  },
  moveDown: {
    transform: 'translate(0, 14px)',
  },
}))

export const CorrespondenceAddress: FC<{
  correspondenceNeeded: boolean
  setCorrespondenceNeeded: React.Dispatch<React.SetStateAction<boolean>>
  contractChanges: ContractProfileChangeAnswers
  correspondenceAddressProps: AddressPropsType | AddressCorrespondencePropsType
}> = ({
  correspondenceNeeded,
  setCorrespondenceNeeded,
  contractChanges,
  correspondenceAddressProps,
}) => {
  const { t } = useTranslation()
  const classes = useStyles()

  const { setValue, watch } = useFormContext()

  const { streetAddress, additionalDetailsOptional, postalCode, city, country } = watch([
    'streetAddress',
    'additionalDetailsOptional',
    'postalCode',
    'city',
    'country',
  ])

  const isCorrespondenceAddressChanged =
    contractChanges?.correspondenceAddress?.country ||
    contractChanges?.correspondenceAddress?.city ||
    contractChanges?.correspondenceAddress?.line1 ||
    contractChanges?.correspondenceAddress?.additionalDetails ||
    contractChanges?.correspondenceAddress?.zip

  const handleCheckbox = useCallback(() => {
    setValue(`streetAddressCorrespondence`, correspondenceNeeded ? streetAddress : '', {
      shouldDirty: true,
    })
    setValue(
      `additionalDetailsOptionalCorrespondence`,
      correspondenceNeeded ? additionalDetailsOptional : '',
      { shouldDirty: true },
    )
    setValue(`postalCodeCorrespondence`, correspondenceNeeded ? postalCode : '', {
      shouldDirty: true,
    })
    setValue(`cityCorrespondence`, correspondenceNeeded ? city : '', { shouldDirty: true })
    setValue(`countryCorrespondence`, correspondenceNeeded ? country : '', {
      shouldDirty: true,
    })
    correspondenceNeeded && setValue('correspondenceRequired', correspondenceNeeded)
    setCorrespondenceNeeded((currentValue) => !currentValue)
  }, [
    correspondenceNeeded,
    setValue,
    watch,
    streetAddress,
    additionalDetailsOptional,
    postalCode,
    city,
    country,
  ])

  return (
    <>
      <Toggler
        name="correspondenceAddress"
        checked={!correspondenceNeeded}
        label={t(
          'correspondenceAddressCheckBoxLabel',
          'Correspondence address is the same as registered',
        )}
        onChange={handleCheckbox}
      />
      <Collapse in={correspondenceNeeded}>
        <AddressAutocomplete
          changedContractProfileData={contractChanges}
          showWarn={false}
          addressLabel={t('correspondenceAddressTitle', 'Correspondence address')}
          correspondence
          props={correspondenceAddressProps}
          fullWidth
          isParagraphLabelSmall
        />
      </Collapse>

      {isCorrespondenceAddressChanged && !correspondenceNeeded && (
        <ControlledTooltipWrapped
          wrapperClass={clsx(classes.dirtyControl, !correspondenceNeeded && classes.moveDown)}
          title={
            <Box>
              <Box
                hidden={!contractChanges?.correspondenceAddress?.countryDescription}
                display="flex"
                gridGap={4}
              >
                <Typography>{t('country', 'Country')}:</Typography>
                <Typography>
                  {contractChanges?.correspondenceAddress?.countryDescription}
                </Typography>
              </Box>

              <Box
                hidden={!contractChanges?.correspondenceAddress?.line1}
                display="flex"
                gridGap={4}
              >
                <Typography>{t('streetAddress', 'Street Address')}:</Typography>
                <Typography>{contractChanges?.correspondenceAddress?.line1}</Typography>
              </Box>

              <Box
                hidden={!contractChanges?.correspondenceAddress?.additionalDetails}
                display="flex"
                gridGap={4}
              >
                <Typography>{t('additionalDetails', 'Additional Details')}:</Typography>
                <Typography>{contractChanges?.correspondenceAddress?.additionalDetails}</Typography>
              </Box>

              <Box hidden={!contractChanges?.correspondenceAddress?.zip} display="flex" gridGap={4}>
                <Typography>{t('postalCode', 'Postal Code')}:</Typography>
                <Typography>{contractChanges?.correspondenceAddress?.zip}</Typography>
              </Box>

              <Box
                hidden={!contractChanges?.correspondenceAddress?.city}
                display="flex"
                gridGap={4}
              >
                <Typography>{t('city', 'City')}:</Typography>
                <Typography>{contractChanges?.correspondenceAddress?.city}</Typography>
              </Box>
            </Box>
          }
        />
      )}
    </>
  )
}
